import { computed, nextTick, watch, type Ref, type ComputedRef } from 'vue';
import { useInfiniteScroll, useDebounceFn, useWindowSize } from '@vueuse/core';

interface UseDocumentInfiniteScrollOptions {
  isLoading: Ref<boolean> | ComputedRef<boolean>;
  hasMore: Ref<boolean> | ComputedRef<boolean>;
  itemCount: Ref<number> | ComputedRef<number>;
  onLoadMore: () => void;
  distanceMultiplier?: number;
}

/**
 * Composable for document-level infinite scrolling with automatic viewport filling.
 */
export function useDocumentInfiniteScroll(options: UseDocumentInfiniteScrollOptions): void {
  const { isLoading, hasMore, itemCount, onLoadMore, distanceMultiplier = 2 } = options;

  const { height } = useWindowSize();
  const scrollDistance = computed(() => height.value * distanceMultiplier);

  // Infinite scroll detection
  useInfiniteScroll(
    document,
    () => {
      if (!isLoading.value && hasMore.value && itemCount.value > 0) {
        onLoadMore();
      }
    },
    {
      distance: scrollDistance.value,
      interval: 200,
      canLoadMore: () => hasMore.value,
    },
  );

  // Debounced check to ensure content fills the viewport and handle "near bottom" state
  const debouncedCheck = useDebounceFn(() => {
    const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = document.documentElement.clientHeight;

    const nearBottom = scrollHeight - scrollTop - clientHeight < scrollDistance.value;
    const doesntFillScreen = scrollHeight > 0 && scrollHeight < clientHeight + scrollDistance.value;

    if ((nearBottom || doesntFillScreen) && !isLoading.value && hasMore.value && itemCount.value > 0) {
      onLoadMore();
    }
  }, 200);

  // Watch for content changes to check if we need to fill the screen
  watch(
    () => [isLoading.value, itemCount.value] as const,
    async ([loading]) => {
      if (!loading && hasMore.value) {
        await nextTick();
        debouncedCheck();
        document.dispatchEvent(new Event('scroll'));
      }
    },
    { immediate: true },
  );
}
